import type { RemoteApiSpecsClient } from "../../clients/remoteApiSpecsClient";
import type { Lens } from "../../lens/Lens";
import type { LensState } from "../../session/lensState";
import { getLogger } from "../../logger/logger";
import type { SessionState } from "../../session/sessionState";
import { namedError } from "../../namedErrors";
import type { CameraKitBootstrapConfiguration, LensHttpHandler, ValidationStrategy } from "../../configuration";
import type { UriHandler, UriRequest, UriResponse } from "../UriHandlers";
import { createUriRequestProcessor } from "../uriRequestProcessor";
import type { RemoteApiSpec } from "../../generated-proto/pb_schema/camera_kit/v3/remote_api_spec";
import { isPromise } from "../../common/typeguards";
import type { ContentType } from "../../lens-core-module/generated-types";
import { requestValidationErrorName, validateRequest } from "./httpValidators";

const logger = getLogger("lensHttpHandler");

const lensHttpHandlerErrorName = "LensHttpHandlerError";
const lensHttpHandlerError = namedError(lensHttpHandlerErrorName);

type LensHttpRequestErrorType = "UnknownError" | "LensHttpHandlerError" | "RequestValidationError";

const allowedResponseHeaders = [
    "Accept-Ranges",
    "Cache-Control",
    "Content-Language",
    "Content-Length",
    "Content-Type",
    "Date",
    "ETag",
    "Expires",
    "Last-Modified",
    "Location",
];

const requestHeadersToExclude = ["x-sc-lenses-remote-api-spec-id"];

/**
 *
 * @internal
 *
 * @param lensState
 * @param sessionState
 * @param remoteApiSpecsClient
 * @param customLentFetchHandler
 * @param validationStrategy
 * @returns
 */
export function createHttpUriHandler(
    lensState: LensState,
    sessionState: SessionState,
    remoteApiSpecsClient: RemoteApiSpecsClient,
    customLentFetchHandler: LensHttpHandler | undefined,
    validationStrategy: ValidationStrategy = "strict"
): UriHandler {
    let allowlistPromise: Promise<RemoteApiSpec[]> | undefined = undefined;

    return createUriRequestProcessor({
        uri: ["http://", "https://"],
        lensState,
        sessionState,
        async processRequest({ request, lens, reply, setCancellationHandler }) {
            allowlistPromise = allowlistPromise ?? getAllowlist(remoteApiSpecsClient);
            const allowlist = await allowlistPromise;
            handleHttpUriRequest(
                request,
                lens,
                reply,
                setCancellationHandler,
                allowlist,
                customLentFetchHandler,
                validationStrategy
            );
        },
        processInternalError(error: Error) {
            // The expectation is that if an error occurs, it happens in our own implementation,
            // because app callbacks are wrapped with try..catch blocks.
            // Therefore, we would like to report this error.
            logger.error(error);
        },
    });
}

async function getAllowlist(remoteApiSpecsClient: RemoteApiSpecsClient) {
    const result = await remoteApiSpecsClient.getRemoteApiSpecs({});
    if (!result.ok) throw new Error("Failed getting Remote API specs.", { cause: result.unwrapErr() });
    return result.unwrap().message?.remoteApiSpecs ?? [];
}

/**
 *
 * @internal
 *
 * @param request
 * @param lens
 * @param reply
 * @param setCancellationHandler
 * @param allowlist
 * @param customLensHttpHandler
 * @param validationStrategy
 */
export async function handleHttpUriRequest(
    request: UriRequest,
    lens: Lens,
    reply: (response: UriResponse) => void,
    setCancellationHandler: (fn: () => void) => void,
    allowlist: RemoteApiSpec[],
    customLensHttpHandler: LensHttpHandler | undefined,
    validationStrategy: ValidationStrategy = "strict"
) {
    try {
        validateRequest(request, allowlist, validationStrategy);
        const { url, init } = mapLensToFetchRequest(request);

        // abort controller to abort fetch request when lens sends cancellation request
        const abortController = new AbortController();
        init.signal = abortController.signal;
        setCancellationHandler(() => abortController.abort());

        let response: Response;
        if (customLensHttpHandler) {
            const handlerName = "lensHttpHandler" satisfies keyof CameraKitBootstrapConfiguration;
            try {
                const { uri, identifier, method, metadata, data } = request;
                const responsePromise = customLensHttpHandler(url, init, {
                    url: uri,
                    identifier,
                    method,
                    data,
                    headers: metadata,
                    lens,
                });
                if (!isPromise(responsePromise)) {
                    throw lensHttpHandlerError(
                        `The '${handlerName}' callback provided to Camera Kit must return a Promise.`
                    );
                }
                response = await responsePromise;
            } catch (error) {
                // NOTE: This error message will be displayed in browser only
                error =
                    error instanceof Error && error.name === lensHttpHandlerErrorName
                        ? error
                        : lensHttpHandlerError(
                              `The '${handlerName}' callback provided to Camera Kit configuration failed.`,
                              error
                          );
                throw error;
            }
        } else {
            response = await fetch(url, init);
        }

        reply(await mapFetchToLensResponse(response));
    } catch (error) {
        if (error instanceof Error && error.name === lensHttpHandlerErrorName) {
            // We do not report errors generated in partners's app code
            logger.warn(error);
            reply(
                getErrorResponse(
                    "LensHttpHandlerError",
                    // In case of an error in partner provided callback,
                    // we just send a generic message to lens, which is common for all Camera Kit SDKs.
                    `The lens HTTP request handler provided to Camera Kit failed.`
                )
            );
        } else if (error instanceof Error && error.name === requestValidationErrorName) {
            // We do not report validation error caused by lens code
            logger.warn(error);
            reply(
                getErrorResponse(
                    "RequestValidationError",
                    // NOTE: Validation errors sent to lens should be consistent with the other Camera Kit SDKs
                    error.message
                )
            );
        } else {
            // We report error to our backend only in case of unknown error
            logger.error(error);
            reply(
                getErrorResponse(
                    "UnknownError",
                    // NOTE: we don't want to expose error details to lens
                    "An unknown error occurred."
                )
            );
        }
    }
}

function getErrorResponse(errorType: LensHttpRequestErrorType, message: string): UriResponse {
    return {
        code: 400,
        description: "",
        contentType: "text/plain",
        metadata: {
            "x-camera-kit-error-type": errorType,
        },
        data: new TextEncoder().encode(message),
    };
}

/**
 * @internal
 */
export function mapLensToFetchRequest({ uri, method, metadata, data }: UriRequest): { url: string; init: RequestInit } {
    const headers = new Headers(metadata);
    // remove internal headers that LensCore adds
    for (const header of requestHeadersToExclude) {
        headers.delete(header);
    }
    return {
        url: uri,
        init: {
            headers: Object.fromEntries(headers.entries()),
            body: method !== "GET" && (method as any) !== "HEAD" && method !== undefined ? data : undefined,
            method,
        },
    };
}

/**
 * @internal
 */
export async function mapFetchToLensResponse(response: Response): Promise<UriResponse> {
    const metadata: Record<string, string> = {};
    for (const allowedHeader of allowedResponseHeaders) {
        const value = response.headers.get(allowedHeader);
        if (value) {
            metadata[allowedHeader.toLowerCase()] = value;
        }
    }
    return {
        code: response.status,
        description: "",
        // Safety: At the time of writing any contentType value provided in the response object will be ignored:
        // eslint-disable-next-line max-len
        // https://github.sc-corp.net/Snapchat/LensCore/blob/722629250973cdf1064baef85d9360229f9dff76/Src/Scenarium/Primitives/RemoteServiceModule.cpp#L517-L533
        // But we still try to provide it with the correct value for consistency with mobile SDKs.
        contentType: (response.headers.get("Content-Type") ?? "") as ContentType,
        metadata,
        data: await response.arrayBuffer(),
    };
}
